import { Transition } from "@headlessui/react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { useState } from "react"

import { FormField, FormFields } from "@/components/form-field"
import HeadingSmall from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import ImageUpload from "@/components/ui/image-upload"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsAccountPath } from "@/routes"
import type { Account, BreadcrumbItem } from "@/types"
import type { Timezone } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Settings",
    href: settingsAccountPath(),
  },
]

export default function Account() {
  const { account, timezones } = usePage<{
    account: Account
    timezones: Timezone[]
  }>().props as { account: Account; timezones: Timezone[] }
  const [preview, setPreview] = useState<string | null>(null)
  const { data, setData, patch, processing, errors, recentlySuccessful } =
    useForm({
      account: {
        name: account.name,
        logo: null as File | null,
        timezone: account.timezone ?? "",
      },
    })

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {
      setData((prev) => ({ ...prev, account: { ...prev.account, logo: file } }))
      setPreview(URL.createObjectURL(file))
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(settingsAccountPath(), {
      forceFormData: true,
    })
  }
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Account details"
            description="Basic information associated with your account."
          />

          <form onSubmit={handleSubmit}>
            <FormFields>
              <FormField
                label="Workspace name"
                htmlFor="name"
                error={(errors as any)?.account?.name ?? undefined}
              >
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={data.account.name}
                  onChange={(e) =>
                    setData((prev) => ({
                      ...prev,
                      account: { ...prev.account, name: e.target.value },
                    }))
                  }
                  autoComplete="name"
                  placeholder="Name"
                />
              </FormField>

              <FormField
                label="Timezone"
                htmlFor="timezone"
                error={(errors as any)?.account?.timezone ?? undefined}
              >
                <Select
                  value={data.account.timezone}
                  onValueChange={(val) =>
                    setData((prev) => ({
                      ...prev,
                      account: { ...prev.account, timezone: val },
                    }))
                  }
                >
                  <SelectTrigger id="timezone" className="w-full max-w-md">
                    <SelectValue placeholder="Select a timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {timezones.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <ImageUpload
                label="Logo"
                id="logo"
                variant="logo"
                preview={preview ?? undefined}
                currentUrl={account.logo_url ?? undefined}
                onFileChange={handleFileChange}
                error={(errors as any)?.account?.logo ?? undefined}
                buttonLabel="Upload logo"
              />

              <div className="flex items-center gap-4">
                <Button disabled={processing}>Save</Button>

                <Transition
                  show={recentlySuccessful}
                  enter="transition ease-in-out"
                  enterFrom="opacity-0"
                  leave="transition ease-in-out"
                  leaveTo="opacity-0"
                >
                  <p className="text-sm text-neutral-600">Saved</p>
                </Transition>
              </div>
            </FormFields>
          </form>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
